import React, { useCallback, useEffect, useState, ReactNode } from 'react';
import AssetLoader, { BeeLoadingVisual } from '../components/AssetLoader';
import { LoadedGameAssets, useGameAssetConfig } from '../assetConfig';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { assetsReadyAtom, assetsVersionAtom, globalAssetsRef } from '../assetStore';

const atomAssetsVersion = assetsVersionAtom;
const atomAssetsReady = assetsReadyAtom;

export const AssetHandler = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const assetConfig = useGameAssetConfig();

  const [, setAssetsVersion] = useAtom(atomAssetsVersion)
  const [assetsReady, setAssetsReady] = useAtom(atomAssetsReady)
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(true)
  const [backgroundProgress, setBackgroundProgress] = useState(0);
  const [backgroundActive, setBackgroundActive] = useState(false);

  const [wrongPwd, Ssee] = useState(false)

  useEffect(() => {
    const pwd = searchParams.get('pwd')
    Ssee(false)
  }, [searchParams])

  useEffect(() => {
    setAssetsReady(false);
    setIsLoading(true);
    setProgress(0);
    setBackgroundProgress(0);
    setBackgroundActive(false);
  }, [setAssetsReady]);

  const handleLoadComplete = (version: number) => {
    setAssetsVersion(version);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!assetsReady)
      return
    setVisible(false)
  }, [assetsReady])

  const handleProgress = (progressValue: number) => {
    setProgress(progressValue);
  };

  const handleBackgroundProgress = (progressValue: number) => {
    setBackgroundProgress(progressValue);
    setBackgroundActive(true);
  };

  useEffect(() => {
    if (backgroundProgress >= 100) {
      const t = setTimeout(() => setBackgroundActive(false), 800);
      return () => clearTimeout(t);
    }
  }, [backgroundProgress]);

  return (
    <>
      {!wrongPwd && (
        <>
          <AssetLoader
            config={assetConfig}
            onLoadComplete={handleLoadComplete}
            onProgress={handleProgress}
            onBackgroundProgress={handleBackgroundProgress}
          />
          {!isLoading && children}
        </>
      )}

      <AnimatePresence>
        {visible && (
          <motion.div 
            className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden bg-[#070a12] text-white"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,188,65,0.08),transparent_32%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.08),transparent_25%),radial-gradient(circle_at_60%_70%,rgba(255,188,65,0.07),transparent_35%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.03)_10%,transparent_20%,transparent_60%,rgba(255,255,255,0.04)_70%)]" />
            <div className="relative flex flex-col items-center gap-12 text-center">
              <BeeLoadingVisual wrongPwd={wrongPwd} progress={progress} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {backgroundActive && !wrongPwd && (
        <div className="fixed inset-x-0 bottom-0 z-10">
          <div className="relative h-1">
            <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
            <div className="absolute inset-0">
              <div
                className="h-full bg-gradient-to-r from-amber-200 via-orange-300 to-amber-500 shadow-[0_0_12px_rgba(255,188,65,0.4)]"
                style={{ width: `${Math.max(0, Math.min(100, backgroundProgress))}%` }}
              />
            </div>
            <div className="absolute right-2 bottom-2 text-[8px] font-semibold uppercase tracking-wide text-amber-100 drop-shadow">
              {Math.max(0, Math.min(100, Math.round(backgroundProgress)))}%
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function useAssets() {
  const version = useAtomValue(atomAssetsVersion)
  const assets = globalAssetsRef.current
  const assetsReady = useAtomValue(atomAssetsReady)
  const setAssetsReady = useSetAtom(atomAssetsReady)
  const markAssetsReady = useCallback(() => setAssetsReady(true), [setAssetsReady])

  return { assets, version, assetsReady, markAssetsReady };
}
